import { useMemo } from 'react';

import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';

import { useSearchParams } from '@pkg/hooks/ReactRouter';

import { type SortingDir } from '@app/client/common/structs';
import {
    type GroupItemOwnership,
    type GroupItemsFilterQuery,
    type GroupItemSorts,
} from '@app/client/common/structs/groups/groups';

export const GROUPS_SEARCH_PARAMS_KEYS = {
    ownership: 'ownership',
    collections: 'collections',
    members: 'members',
    dir: 'direction',
    sort: 'sort',
    spam: 'spam',
} as const;

export const DEFAULT_SORT_DIR: SortingDir = 'Desc';
export const DEFAULT_SORT: GroupItemSorts = 'purchase_date';

// TODO: should we not filter out invalid values? And instead show the error response to the user?

/**
 * React Hook to get a group items filter query from the search params. It will remove certain invalid values.
 * Supports the following search params:
 * - ownership
 * - collections
 * - members
 * - sort direction
 * - sort
 * - spam
 */
// TODO: This hook should be phased out and removed in favour of `useNftFilter()`.
export const useGroupFilterParams = (): GroupItemsFilterQuery | undefined => {
    const [searchParams] = useSearchParams();

    return useMemo(() => {
        /**
         * ownership value from search parameters. Filter out invalid values and return the first value.
         * API only supports one value.
         */
        // TODO: We should not be converting the casing back-and-forth.
        // Let's be consistent and always use the same casing.
        const ownershipValue: GroupItemsFilterQuery['ownership'] | undefined = searchParams
            .getAll(GROUPS_SEARCH_PARAMS_KEYS.ownership)
            .flatMap((e) => e.split(','))
            .map(capitalize)
            .filter((item): item is GroupItemOwnership => isValidOwnership(item));

        /**
         * collections values from search parameters.
         */
        const collectionsValues: GroupItemsFilterQuery['contract_addresses'] | undefined = searchParams
            .getAll(GROUPS_SEARCH_PARAMS_KEYS.collections)
            .flatMap((e) => e.split(','));

        /**
         *  members values  from search parameters.
         */
        const membersValues: GroupItemsFilterQuery['members'] | undefined = searchParams
            .getAll(GROUPS_SEARCH_PARAMS_KEYS.members)
            .flatMap((e) => e.split(','));

        /**
         * direction value Capitalize the first character. Filter invalid values. Return the first valid value.
         * TODO: @andykenward Do we need a default value for the UI to show, 'Asc'?
         * The pagination response has this in pagination.dir. Could use that?
         */
        const sortDirValue: GroupItemsFilterQuery['dir'] | undefined = searchParams
            .getAll(GROUPS_SEARCH_PARAMS_KEYS.dir)
            .flatMap((e) => e.split(','))
            .map(capitalize)
            .filter(isSortingDir)[0];

        /**
         * sort value. Filter invalid values. Return the first valid value.
         */
        const sortValue: GroupItemsFilterQuery['sort'] | undefined = searchParams
            .getAll(GROUPS_SEARCH_PARAMS_KEYS.sort)
            .flatMap((e) => e.split(','))
            .filter(isValidSort)[0];

        /**
         * spam boolean filter
         */
        const spamValue: GroupItemsFilterQuery['spam'] | undefined =
            searchParams.getAll(GROUPS_SEARCH_PARAMS_KEYS.spam).flatMap((e) => e.split(','))[0] === 'true';

        /**
         * filters is the object that will be returned.
         * We remove object keys if values are undefined.
         */
        const filters = omitBy<GroupItemsFilterQuery>(
            {
                ownership: ownershipValue.length ? ownershipValue : undefined,
                contract_addresses: collectionsValues.length ? collectionsValues : undefined,
                members: membersValues.length ? membersValues : undefined,
                dir: sortDirValue || DEFAULT_SORT_DIR || undefined,
                sort: sortValue || undefined,
                spam: spamValue !== undefined ? spamValue : undefined,
            },
            isNil,
        );
        /**
         * Check if filters are empty.
         */
        return isEmpty(filters) ? undefined : filters;
    }, [searchParams]);
};

// TODO: not sure I like the way this is done. Must be a better may to cover all values of GroupItemOwnership.
export const OWNERSHIPS: ReadonlyArray<GroupItemOwnership> = ['Sold', 'Purchased', 'Minted', 'Transferred'];
/**
 * Supported values for ownership
 */
const isValidOwnership = (ownership: string): ownership is GroupItemOwnership => {
    return OWNERSHIPS.includes(ownership as never);
};

const isSortingDir = (item: string): item is SortingDir => item === 'Asc' || item === 'Desc';

export const SORTS: ReadonlyArray<GroupItemSorts> = [
    'id',
    'user_id',
    'yat_page_id',
    'purchase_date',
    'purchase_price',
    'purchase_unit',
    'published_at',
    'created_at',
    'floor_price',
    'verified',
    'minter',
];
/**
 * Supported values for ownership
 */
const isValidSort = (sort: string): sort is GroupItemSorts => {
    return SORTS.includes(sort as never);
};
